import * as yup from 'yup';
import {
  AI_ACCEPTED_EXTENSIONS,
  AI_DOCUMENT_TAG_OPTIONS,
  AI_FILE_TAG_LIMIT,
  AI_FILE_TAG_MAX_LENGTH,
  AI_MAX_FILE_SIZE_BYTES,
  AI_MAX_FILE_SIZE_MB,
  AI_MAX_FILENAME_LENGTH
} from './constants';

export const PROCESS_SOURCE = {
  AI_ASSISTED: 'ai_assisted',
  MANUAL: 'manual',
  BATCH_IMPORT: 'batch_import'
};

export const createProcessSchema = yup
  .object({
    name: yup
      .string()
      .transform((value) => (value ?? '').trim())
      .required('Process name is required')
      .min(3, 'Process name must be at least 3 characters')
      .max(255, 'Process name cannot be more than 255 characters'),
    description: yup
      .string()
      .transform((value) => (value ?? '').trim())
      .max(1000, 'Description cannot be more than 1000 characters')
      .test(
        'description-min-when-provided',
        'Description must be at least 3 characters',
        (value) => !value || value.length >= 3
      ),
    owner: yup
      .string()
      .transform((value) => (value ?? '').trim())
      .max(255, 'Owner cannot be more than 255 characters'),
    actors: yup.string().default(''),
    systems: yup.string().default('')
  })
  .required();

export const processSubmitSchema = yup
  .object({
    id: yup.string().optional(),
    name: yup
      .string()
      .transform((value) => (value ?? '').trim())
      .required('Process name is required')
      .min(3, 'Process name must be at least 3 characters')
      .max(255, 'Process name cannot be more than 255 characters'),
    description: yup
      .string()
      .transform((value) => (value ?? '').trim())
      .required('Description is required')
      .min(3, 'Description must be at least 3 characters')
      .max(1000, 'Description cannot be more than 1000 characters'),
    owner: yup
      .string()
      .transform((value) => (value ?? '').trim())
      .max(255, 'Owner cannot be more than 255 characters'),
    actors: yup.array().of(yup.string().trim()).default([]),
    systems: yup.array().of(yup.string().trim()).default([]),
    risks: yup.array().of(yup.string().trim()).default([]),
    controls: yup.array().of(yup.string().trim()).default([]),
    source: yup
      .string()
      .oneOf(Object.values(PROCESS_SOURCE))
      .required()
  })
  .required();

export const aiProcessDocumentTagSchema = yup
  .string()
  .transform((value) => String(value ?? '').trim())
  .required('Enter a tag name')
  .oneOf(
    AI_DOCUMENT_TAG_OPTIONS.map((option) => option.value),
    'Select a valid document tag'
  )
  .max(AI_FILE_TAG_MAX_LENGTH, `Tags must be ${AI_FILE_TAG_MAX_LENGTH} characters or fewer`);

export const aiProcessDocumentTagsSchema = yup
  .array()
  .of(aiProcessDocumentTagSchema)
  .max(AI_FILE_TAG_LIMIT, `Use up to ${AI_FILE_TAG_LIMIT} tags per file`)
  .test('unique-tags', 'Tag already exists for this file', (tags = []) => {
    const normalized = tags.map((tag) => String(tag || '').trim().toLowerCase()).filter(Boolean);
    return normalized.length === new Set(normalized).size;
  });

export const aiProcessDocumentFileSchema = yup
  .mixed()
  .required('Select a PDF or DOCX file')
  .test('file-type', 'Only PDF and DOCX files are supported.', (file) => {
    const ext = String(file?.name || '').split('.').pop()?.toLowerCase() || '';
    return AI_ACCEPTED_EXTENSIONS.includes(ext);
  })
  .test(
    'file-name-length',
    `File name must be ${AI_MAX_FILENAME_LENGTH} characters or fewer.`,
    (file) => String(file?.name || '').length <= AI_MAX_FILENAME_LENGTH
  )
  .test(
    'file-size',
    `Each file must be ${AI_MAX_FILE_SIZE_MB} MB or smaller.`,
    (file) => Number(file?.size || 0) <= AI_MAX_FILE_SIZE_BYTES
  );
